import { useEffect, useState } from 'react';
import { Ellipsis } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { PlantProductionAndStorageEnvironmentalBenefits } from '@/features/plants/components/plant-overview/production-and-storage/shared/plant-production-and-storage-environmental-benefits';
import { PlantProductionAndStorageTodayMetrics } from '@/features/plants/components/plant-overview/production-and-storage/shared/plant-production-and-storage-today-metrics';
import { PlantProductionAndStorageRealtimePlantMetricsDisplay } from '@/features/plants/components/plant-overview/production-and-storage/shared/plant-production-and-storage-realtime-plant-metrics-display';
import { useGetPlantOverviewQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-overview.ts';
import { PlantProductionAndStorageEnergyManagement } from '@/features/plants/components/plant-overview/production-and-storage/shared/plant-production-and-storage-energy-management';
import { useGetPlantEnergyManagementChartQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-energy-management-chart';
import { MultiDeviceMetricsTableCard } from '@/features/devices/components/multi-device-metrics-table/multi-device-metrics-table-card';
import { useGetPlantDevicesOverviewQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-devices-overview';
import { useSession } from '@/services/providers/session-provider';
import { getTodayDateRange } from '@/utils/get-today-date-range';

import style from '@/features/plants/components/plant-overview/production-and-storage/basic/plant-production-and-storage-overview-basic.module.scss';
import { PlantMenuPopoverGenerateReportDialog } from '../../plant-menu-popover-generate-report-dialog';
import { usePermission } from '@/hooks/use-permission';
import { cn } from '@/utils/utils';

export const PlantProductionAndStorageOverviewBasic = ({
  plantId,
}: {
  plantId: string | undefined;
}) => {
  const { role } = useSession();
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

  const { data: overview, isLoading: isPlantOverviewLoading } = useGetPlantOverviewQuery({
    plantId: plantId,
  });

  const { isLoading: isChartPlantLoading } = useGetPlantEnergyManagementChartQuery({
    plantId,
    dateRange: {
      ...getTodayDateRange(),
      rangeGranularity: '5min',
    },
    enabled: Boolean(plantId),
  });

  const shouldShowDevicesOverview = role === 'Owner';

  const canViewGenerateReport = usePermission({
    resource: 'plants',
    action: 'view-generate-report.plant',
  });

  const { data: devicesOverview, isLoading: isDevicesOverviewLoading } =
    useGetPlantDevicesOverviewQuery({
      plantId: plantId,
      dateRange: {
        startDate: new Date(new Date().setHours(0, 0, 0, 0)),
        endDate: new Date(new Date().setHours(23, 59, 59, 999)),
      },
      enabled: Boolean(plantId) && shouldShowDevicesOverview,
    });

  const isLoading = isPlantOverviewLoading || isChartPlantLoading || !overview;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [plantId]);

  return (
    <div className={`gap-6 ${style.plant_grid_container}`}>
      <div className={`gap-6 ${style.plant_grid}`}>
        <PlantProductionAndStorageTodayMetrics
          overview={overview}
          isLoading={isLoading}
          className={style.today_metrics}
        />

        <PlantProductionAndStorageRealtimePlantMetricsDisplay
          overview={overview}
          isLoading={isLoading}
          className={style.realtime_metrics}
        />

        <PlantProductionAndStorageEnergyManagement
          plantId={plantId}
          isLoading={isLoading}
          className={style.energy_management_chart}
        >

          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="secondary" className="cursor-pointer rounded-lg bg-gray-100 p-2">
                <Ellipsis />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className={cn(
                'relative mr-4 w-55 overflow-hidden rounded-lg border bg-white/95 p-1 shadow-xl backdrop-blur-xl dark:bg-gray-900/95'
              )}
            >
              {canViewGenerateReport && (
                <PlantMenuPopoverGenerateReportDialog plantId={plantId} />
              )}
            </PopoverContent>
          </Popover>

        </PlantProductionAndStorageEnergyManagement>

        <PlantProductionAndStorageEnvironmentalBenefits
          overview={overview}
          isLoading={isLoading}
          className={style.environmental_benefits}
        />

        {shouldShowDevicesOverview && Boolean(devicesOverview?.production?.length) && (
          <MultiDeviceMetricsTableCard
            data={devicesOverview?.production}
            type="production"
            isLoading={isLoading || isDevicesOverviewLoading}
            plantId={plantId || ''}
            title="Overview Production"
            className={style.production}
            expandable={{
              isExpandable: true,
              defaultExpanded: false,
            }}
          />
        )}
        {shouldShowDevicesOverview && Boolean(devicesOverview?.storage?.length) && (
          <MultiDeviceMetricsTableCard
            data={devicesOverview?.storage}
            type="storage"
            isLoading={isLoading || isDevicesOverviewLoading}
            plantId={plantId || ''}
            title="Overview Battery"
            className={style.battery}
            expandable={{
              isExpandable: true,
              defaultExpanded: false,
            }}
          />
        )}
      </div>
    </div>
  );
};
